import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { createVariants, cn } from '../../lib/utils';
import { SpinnerIcon } from './Icons';

// Button variants using your theme system (focus ring color applied via theme in component)
const buttonVariants = createVariants(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variant: {
      default: '',
      secondary: '',
      accent: '',
      danger: '',
      warning: '',
      info: '',
      outline: '',
      ghost: 'hover:opacity-80',
      link: 'underline-offset-4 hover:underline p-0',
    },
    size: {
      default: 'px-4 py-2 h-10',
      sm: 'px-3 py-1.5 h-8 text-xs',
      lg: 'px-6 py-3 h-12 text-base',
      icon: 'h-10 w-10 p-0',
    },
  },
  {
    variant: 'default',
    size: 'default',
  }
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' | 'outline' | 'ghost' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  loading?: boolean;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', children, disabled, loading, ...props }, ref) => {
    const { colors } = useTheme();

    // Get base variant classes
    const baseClasses = buttonVariants({ variant, size, className });

    // Apply theme-specific styling based on variant
    const getVariantClasses = () => {
      switch (variant) {
        case 'default':
          return `${colors.primary} ${colors.primaryText} hover:opacity-90 shadow-sm`;
        case 'secondary':
          return `${colors.surfaceHover} ${colors.text} hover:opacity-90 shadow-sm`;
        case 'accent':
          return `${colors.accent} ${colors.primaryText} hover:opacity-90 shadow-sm`;
        case 'danger':
          return `${colors.error} hover:opacity-90 shadow-sm`;
        case 'warning':
          return `${colors.warning} hover:opacity-90 shadow-sm`;
        case 'info':
          return `${colors.info} hover:opacity-90 shadow-sm`;
        case 'outline':
          return `${colors.surface} ${colors.text} border ${colors.border} hover:${colors.surfaceHover} shadow-sm`;
        case 'ghost':
          return `${colors.text} hover:${colors.surfaceHover}`;
        case 'link':
          return `${colors.text}`;
        default:
          return `${colors.primary} ${colors.primaryText} hover:opacity-90 shadow-sm`;
      }
    };

    const variantClasses = getVariantClasses();
    const focusRingClass = colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '';
    const finalClasses = cn(baseClasses, variantClasses, 'focus:ring-2 focus:ring-offset-2', focusRingClass);

    return (
      <button className={finalClasses} ref={ref} disabled={disabled || loading} {...props}>
        {loading && <SpinnerIcon className="w-4 h-4" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };

